"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { Award, CalendarCheck, PackageSearch, Sparkles, Tag, TrendingDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { OrganizationRanking } from "@/entities/organization/api/organizations";
import { MetricChip, type OrganizationMetricDrilldownTarget } from "@/entities/organization/ui/organization-metric-chip";
import { formatCount } from "@/shared/lib/format/numbers";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/badge";
import { EntityAvatar } from "@/shared/ui/entity-avatar";
import { Progress } from "@/shared/ui/progress";
function pct(value: number) {
  return `${Math.round(value * 100)}%`;
}
function ScoreBar(t0) {
  const $ = _c(14);
  const {
    label,
    value,
    max: t1
  } = t0;
  const max = t1 === undefined ? 5 : t1;
  const pctValue = Math.max(0, Math.min(100, value / max * 100));
  let t2;
  if ($[0] !== label) {
    t2 = <span className="truncate">{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== value) {
    t3 = value.toFixed(1);
    $[2] = value;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span className="font-medium tabular-nums">{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className="text-muted-foreground mb-1 flex items-center justify-between text-[10.5px] leading-none">{t2}{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== pctValue) {
    t6 = <Progress value={pctValue} className="h-1" />;
    $[9] = pctValue;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="min-w-0">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
function overallTone(overall: number) {
  if (overall >= 4) return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
  if (overall >= 3) return "bg-sky-500/15 text-sky-700 dark:text-sky-300";
  if (overall >= 2) return "bg-amber-500/15 text-amber-700 dark:text-amber-300";
  return "bg-muted text-muted-foreground";
}
export function OrganizationRankingRow(t0) {
  const $ = _c(170);
  const {
    rank,
    ranking,
    onMetricClick
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    metrics,
    ratios,
    score
  } = ranking;
  let T0;
  let promotionPct;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== metrics.cheapest_count || $[1] !== metrics.distinct_medicines || $[2] !== metrics.exclusive_medicines || $[3] !== onMetricClick || $[4] !== rank || $[5] !== ranking || $[6] !== ratios.cheapest_pct || $[7] !== ratios.promotion_pct || $[8] !== score.freshness || $[9] !== score.medicines || $[10] !== score.overall || $[11] !== score.price || $[12] !== score.promotion || $[13] !== score.variety || $[14] !== t) {
    const cheapestPct = pct(ratios.cheapest_pct);
    promotionPct = pct(ratios.promotion_pct);
    t13 = "group border-border/60 hover:bg-muted/30 relative border-b px-4 py-3 transition-colors last:border-0";
    if ($[31] !== onMetricClick || $[32] !== ranking.name || $[33] !== ranking.slug) {
      t14 = ranking.slug && onMetricClick ? <Link prefetch={false} href={`/app/organizations/${ranking.slug}`} aria-label={ranking.name} className="focus-visible:ring-ring/40 absolute inset-0 z-0 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset" /> : null;
      $[31] = onMetricClick;
      $[32] = ranking.name;
      $[33] = ranking.slug;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    t10 = "pointer-events-none flex items-start gap-3";
    if ($[35] !== rank) {
      t11 = <div className="text-muted-foreground w-7 shrink-0 pt-1 text-center text-xs font-semibold tabular-nums">{rank}</div>;
      $[35] = rank;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    if ($[37] !== ranking) {
      t12 = <EntityAvatar entity={ranking} size="sm" />;
      $[37] = ranking;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    t7 = "min-w-0 flex-1";
    let t15;
    if ($[39] !== ranking.name) {
      t15 = <p className="text-foreground truncate text-sm leading-snug font-medium">{ranking.name}</p>;
      $[39] = ranking.name;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    const t16 = ranking.city ?? ranking.region ?? "\u2014";
    let t17;
    if ($[41] !== t16) {
      t17 = <p className="text-muted-foreground mt-0.5 truncate text-[11.5px]">{t16}</p>;
      $[41] = t16;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    let t18;
    if ($[43] !== t15 || $[44] !== t17) {
      t18 = <div className="min-w-0">{t15}{t17}</div>;
      $[43] = t15;
      $[44] = t17;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    let t19;
    if ($[46] !== score.overall) {
      t19 = cn("shrink-0 gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", overallTone(score.overall));
      $[46] = score.overall;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    let t20;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <Award className="size-3" />;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    let t21;
    if ($[49] !== score.overall) {
      t21 = score.overall.toFixed(1);
      $[49] = score.overall;
      $[50] = t21;
    } else {
      t21 = $[50];
    }
    let t22;
    if ($[51] !== t19 || $[52] !== t21) {
      t22 = <Badge className={t19} variant="secondary">{t20}{t21}</Badge>;
      $[51] = t19;
      $[52] = t21;
      $[53] = t22;
    } else {
      t22 = $[53];
    }
    if ($[54] !== t18 || $[55] !== t22) {
      t8 = <div className="flex items-start justify-between gap-3">{t18}{t22}</div>;
      $[54] = t18;
      $[55] = t22;
      $[56] = t8;
    } else {
      t8 = $[56];
    }
    let t23;
    if ($[57] !== t) {
      t23 = t("organizations.ratings.scoreLabels.price");
      $[57] = t;
      $[58] = t23;
    } else {
      t23 = $[58];
    }
    let t24;
    if ($[59] !== score.price || $[60] !== t23) {
      t24 = <ScoreBar label={t23} value={score.price} />;
      $[59] = score.price;
      $[60] = t23;
      $[61] = t24;
    } else {
      t24 = $[61];
    }
    let t25;
    if ($[62] !== t) {
      t25 = t("organizations.ratings.scoreLabels.variety");
      $[62] = t;
      $[63] = t25;
    } else {
      t25 = $[63];
    }
    let t26;
    if ($[64] !== score.variety || $[65] !== t25) {
      t26 = <ScoreBar label={t25} value={score.variety} />;
      $[64] = score.variety;
      $[65] = t25;
      $[66] = t26;
    } else {
      t26 = $[66];
    }
    let t27;
    if ($[67] !== t) {
      t27 = t("organizations.ratings.scoreLabels.medicines");
      $[67] = t;
      $[68] = t27;
    } else {
      t27 = $[68];
    }
    let t28;
    if ($[69] !== score.medicines || $[70] !== t27) {
      t28 = <ScoreBar label={t27} value={score.medicines} />;
      $[69] = score.medicines;
      $[70] = t27;
      $[71] = t28;
    } else {
      t28 = $[71];
    }
    let t29;
    if ($[72] !== t) {
      t29 = t("organizations.ratings.scoreLabels.freshness");
      $[72] = t;
      $[73] = t29;
    } else {
      t29 = $[73];
    }
    let t30;
    if ($[74] !== score.freshness || $[75] !== t29) {
      t30 = <ScoreBar label={t29} value={score.freshness} />;
      $[74] = score.freshness;
      $[75] = t29;
      $[76] = t30;
    } else {
      t30 = $[76];
    }
    let t31;
    if ($[77] !== t) {
      t31 = t("organizations.ratings.scoreLabels.promotions");
      $[77] = t;
      $[78] = t31;
    } else {
      t31 = $[78];
    }
    let t32;
    if ($[79] !== score.promotion || $[80] !== t31) {
      t32 = <ScoreBar label={t31} value={score.promotion} />;
      $[79] = score.promotion;
      $[80] = t31;
      $[81] = t32;
    } else {
      t32 = $[81];
    }
    if ($[82] !== t24 || $[83] !== t26 || $[84] !== t28 || $[85] !== t30 || $[86] !== t32) {
      t9 = <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3 lg:grid-cols-5">{t24}{t26}{t28}{t30}{t32}</div>;
      $[82] = t24;
      $[83] = t26;
      $[84] = t28;
      $[85] = t30;
      $[86] = t32;
      $[87] = t9;
    } else {
      t9 = $[87];
    }
    t3 = "text-muted-foreground relative z-10 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]";
    let t33;
    if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <PackageSearch className="size-3" />;
      $[88] = t33;
    } else {
      t33 = $[88];
    }
    let t34;
    if ($[89] !== metrics.distinct_medicines || $[90] !== t) {
      t34 = t("organizations.ratings.metrics.medicines", {
        count: formatCount(metrics.distinct_medicines)
      });
      $[89] = metrics.distinct_medicines;
      $[90] = t;
      $[91] = t34;
    } else {
      t34 = $[91];
    }
    if ($[92] !== t34) {
      t4 = <span className="inline-flex items-center gap-1">{t33}{t34}</span>;
      $[92] = t34;
      $[93] = t4;
    } else {
      t4 = $[93];
    }
    let t35;
    if ($[94] !== metrics.exclusive_medicines || $[95] !== t) {
      t35 = t("organizations.ratings.metrics.exclusiveMedicines", {
        count: formatCount(metrics.exclusive_medicines)
      });
      $[94] = metrics.exclusive_medicines;
      $[95] = t;
      $[96] = t35;
    } else {
      t35 = $[96];
    }
    let t36;
    if ($[97] !== metrics.exclusive_medicines || $[98] !== ranking.name || $[99] !== t) {
      t36 = t("organizations.ratings.drilldown.openAria", {
        count: metrics.exclusive_medicines,
        metric: t("organizations.ratings.drilldown.title.exclusive"),
        organization: ranking.name
      });
      $[97] = metrics.exclusive_medicines;
      $[98] = ranking.name;
      $[99] = t;
      $[100] = t36;
    } else {
      t36 = $[100];
    }
    const t37 = metrics.exclusive_medicines <= 0;
    let t38;
    if ($[101] !== metrics.exclusive_medicines || $[102] !== onMetricClick || $[103] !== ranking.name || $[104] !== ranking.organization_id) {
      t38 = onMetricClick ? () => onMetricClick({
        organizationId: ranking.organization_id,
        organizationName: ranking.name,
        metric: "exclusive",
        expectedCount: metrics.exclusive_medicines,
        expectedPctLabel: null
      }) : undefined;
      $[101] = metrics.exclusive_medicines;
      $[102] = onMetricClick;
      $[103] = ranking.name;
      $[104] = ranking.organization_id;
      $[105] = t38;
    } else {
      t38 = $[105];
    }
    if ($[106] !== t35 || $[107] !== t36 || $[108] !== t37 || $[109] !== t38) {
      t5 = <MetricChip icon={Sparkles} label={t35} ariaLabel={t36} disabled={t37} onClick={t38} />;
      $[106] = t35;
      $[107] = t36;
      $[108] = t37;
      $[109] = t38;
      $[110] = t5;
    } else {
      t5 = $[110];
    }
    const t39 = t("organizations.ratings.metrics.cheapest", {
      pct: cheapestPct
    });
    let t40;
    if ($[111] !== metrics.cheapest_count || $[112] !== ranking.name || $[113] !== t) {
      t40 = t("organizations.ratings.drilldown.openAria", {
        count: metrics.cheapest_count,
        metric: t("organizations.ratings.drilldown.title.cheapest"),
        organization: ranking.name
      });
      $[111] = metrics.cheapest_count;
      $[112] = ranking.name;
      $[113] = t;
      $[114] = t40;
    } else {
      t40 = $[114];
    }
    const t41 = metrics.cheapest_count <= 0;
    let t42;
    if ($[115] !== cheapestPct || $[116] !== metrics.cheapest_count || $[117] !== onMetricClick || $[118] !== ranking.name || $[119] !== ranking.organization_id) {
      t42 = onMetricClick ? () => onMetricClick({
        organizationId: ranking.organization_id,
        organizationName: ranking.name,
        metric: "cheapest",
        expectedCount: metrics.cheapest_count,
        expectedPctLabel: cheapestPct
      }) : undefined;
      $[115] = cheapestPct;
      $[116] = metrics.cheapest_count;
      $[117] = onMetricClick;
      $[118] = ranking.name;
      $[119] = ranking.organization_id;
      $[120] = t42;
    } else {
      t42 = $[120];
    }
    if ($[121] !== t39 || $[122] !== t40 || $[123] !== t41 || $[124] !== t42) {
      t6 = <MetricChip icon={TrendingDown} label={t39} ariaLabel={t40} disabled={t41} onClick={t42} />;
      $[121] = t39;
      $[122] = t40;
      $[123] = t41;
      $[124] = t42;
      $[125] = t6;
    } else {
      t6 = $[125];
    }
    T0 = MetricChip;
    t1 = Tag;
    t2 = t("organizations.ratings.metrics.promo", {
      pct: promotionPct
    });
    $[0] = metrics.cheapest_count;
    $[1] = metrics.distinct_medicines;
    $[2] = metrics.exclusive_medicines;
    $[3] = onMetricClick;
    $[4] = rank;
    $[5] = ranking;
    $[6] = ratios.cheapest_pct;
    $[7] = ratios.promotion_pct;
    $[8] = score.freshness;
    $[9] = score.medicines;
    $[10] = score.overall;
    $[11] = score.price;
    $[12] = score.promotion;
    $[13] = score.variety;
    $[14] = t;
    $[15] = T0;
    $[16] = promotionPct;
    $[17] = t1;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    T0 = $[15];
    promotionPct = $[16];
    t1 = $[17];
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t13 = $[21];
    t14 = $[22];
    t2 = $[23];
    t3 = $[24];
    t4 = $[25];
    t5 = $[26];
    t6 = $[27];
    t7 = $[28];
    t8 = $[29];
    t9 = $[30];
  }
  let t15;
  if ($[126] !== metrics.promotion_count || $[127] !== ranking.name || $[128] !== t) {
    t15 = t("organizations.ratings.drilldown.openAria", {
      count: metrics.promotion_count,
      metric: t("organizations.ratings.drilldown.title.promotion"),
      organization: ranking.name
    });
    $[126] = metrics.promotion_count;
    $[127] = ranking.name;
    $[128] = t;
    $[129] = t15;
  } else {
    t15 = $[129];
  }
  const t16 = metrics.promotion_count <= 0;
  let t17;
  if ($[130] !== metrics.promotion_count || $[131] !== onMetricClick || $[132] !== promotionPct || $[133] !== ranking.name || $[134] !== ranking.organization_id) {
    t17 = onMetricClick ? () => onMetricClick({
      organizationId: ranking.organization_id,
      organizationName: ranking.name,
      metric: "promotion",
      expectedCount: metrics.promotion_count,
      expectedPctLabel: promotionPct
    }) : undefined;
    $[130] = metrics.promotion_count;
    $[131] = onMetricClick;
    $[132] = promotionPct;
    $[133] = ranking.name;
    $[134] = ranking.organization_id;
    $[135] = t17;
  } else {
    t17 = $[135];
  }
  let t18;
  if ($[136] !== T0 || $[137] !== t1 || $[138] !== t15 || $[139] !== t16 || $[140] !== t17 || $[141] !== t2) {
    t18 = <T0 icon={t1} label={t2} ariaLabel={t15} disabled={t16} onClick={t17} />;
    $[136] = T0;
    $[137] = t1;
    $[138] = t15;
    $[139] = t16;
    $[140] = t17;
    $[141] = t2;
    $[142] = t18;
  } else {
    t18 = $[142];
  }
  let t19;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <CalendarCheck className="size-3" />;
    $[143] = t19;
  } else {
    t19 = $[143];
  }
  let t20;
  if ($[144] !== metrics.active_days || $[145] !== t) {
    t20 = t("organizations.ratings.metrics.daysActive", {
      days: metrics.active_days
    });
    $[144] = metrics.active_days;
    $[145] = t;
    $[146] = t20;
  } else {
    t20 = $[146];
  }
  let t21;
  if ($[147] !== t20) {
    t21 = <span className="inline-flex items-center gap-1">{t19}{t20}</span>;
    $[147] = t20;
    $[148] = t21;
  } else {
    t21 = $[148];
  }
  let t22;
  if ($[149] !== t18 || $[150] !== t21 || $[151] !== t3 || $[152] !== t4 || $[153] !== t5 || $[154] !== t6) {
    t22 = <div className={t3}>{t4}{t5}{t6}{t18}{t21}</div>;
    $[149] = t18;
    $[150] = t21;
    $[151] = t3;
    $[152] = t4;
    $[153] = t5;
    $[154] = t6;
    $[155] = t22;
  } else {
    t22 = $[155];
  }
  let t23;
  if ($[156] !== t22 || $[157] !== t7 || $[158] !== t8 || $[159] !== t9) {
    t23 = <div className={t7}>{t8}{t9}{t22}</div>;
    $[156] = t22;
    $[157] = t7;
    $[158] = t8;
    $[159] = t9;
    $[160] = t23;
  } else {
    t23 = $[160];
  }
  let t24;
  if ($[161] !== t10 || $[162] !== t11 || $[163] !== t12 || $[164] !== t23) {
    t24 = <div className={t10}>{t11}{t12}{t23}</div>;
    $[161] = t10;
    $[162] = t11;
    $[163] = t12;
    $[164] = t23;
    $[165] = t24;
  } else {
    t24 = $[165];
  }
  let t25;
  if ($[166] !== t13 || $[167] !== t14 || $[168] !== t24) {
    t25 = <div className={t13}>{t14}{t24}</div>;
    $[166] = t13;
    $[167] = t14;
    $[168] = t24;
    $[169] = t25;
  } else {
    t25 = $[169];
  }
  return t25;
}